import { useState, useEffect, useCallback } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';

// Define types for content and diary items
export interface ContentItem {
  type: string;
  text?: string;
  hash?: string;
  path?: string;
  thumbnail?: string;
  s3Url?: string;
}

export interface DiaryItem {
  id: string;
  title?: string;
  description?: string;
  lastAssistantMessage?: string;
  threadId?: string;
  interpretationObj?:
    | {
        description?: string;
      }
    | string;
  category?: string;
  content: Array<ContentItem>;
  createdAt: number; // Use number type for compatibility with navigation
  paymentDate?: string | number; // Primary date field for month assignment
  endDate?: string | number; // Fallback date field
  startDate?: string | number; // Fallback date field
  state?: string;
  status?: string;
  type?: string; // Added type property
  metadata?: string; // Added metadata field
  s3Url?: string; // S3 URL for images
  hash?: string; // Hash for image caching
  diaryId?: string; // Diary ID for category updates
  index?: number; // Index for rendering
  // Keep existing fields for backward compatibility
  timestamp?: number;
  created_at?: number;
  // Additional fields for DocumentGridItem compatibility
  lastAssistantMessageTimestamp?: number;
  last_read_data?: string | { user_id: string; timestamp: number };
  amount?: number;
  highlightedTitle?: string;
  highlightedContent?: string;
}

export const useDiaryItems = () => {
  const [diaryItems, setDiaryItems] = useState<DiaryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOrganisation] = useActiveOrganisation();

  const fetchDiaryItems = useCallback(async () => {
    if (!activeOrganisation?.organizationId) return;

    try {
      // Get access token
      const tokenResponse = await fetch('/access-token');
      const { accessToken } = await tokenResponse.json();

      const response = await fetch('/api/diaries', {
        headers: {
          'X-Organization': activeOrganisation.organizationId,
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch diary items');
      }

      const data = await response.json();
      setDiaryItems(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [activeOrganisation?.organizationId]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchDiaryItems();
  }, [fetchDiaryItems]);

  // Refetch function
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    await fetchDiaryItems();
    setIsRefetching(false);
  }, [fetchDiaryItems]);

  return {
    diaryItems,
    error,
    refetch,
    isRefetching,
    isLoading,
  };
};
